import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import supabase from '../../../supabaseClient';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiEdit2, FiX, FiCloud, FiCheckCircle, FiBuilding, FiMail, FiMapPin, FiUsers, FiTag } = FiIcons;

const CompanyProfile = () => {
  const { companies, updateCompany } = useData();
  const { user } = useAuth();
  const [currentCompany, setCurrentCompany] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    org_number: '',
    email: '',
    contact_person: '',
    address: '',
    employees: 0,
    industry: '',
  });
  const [publishStatus, setPublishStatus] = useState('');

  // SIKKERHETSSJEKK: Finn bedriften basert på brukeren
  useEffect(() => {
    if (user && companies.length > 0) {
      const found = companies.find(c => c.id === (user.companyId || user.company_id));
      if (found) {
        setCurrentCompany(found);
        setFormData({
          name: found.name || '',
          org_number: found.org_number || found.orgNumber || '',
          email: found.email || found.contact_email || '',
          contact_person: found.contact_person || found.contactName || '',
          address: found.address || found.raw_data?.address || '',
          employees: found.employees || 0,
          industry: found.industry || '',
        });
      }
      setIsLoading(false); // Stopp lasting uansett
    } else {
      // Timeout hvis data ikke kommer
      const timer = setTimeout(() => setIsLoading(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [user, companies]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentCompany) {
      await updateCompany(currentCompany.id, formData);
      setIsEditing(false);
    }
  };

  const handlePublishToCloud = async () => {
    if (!currentCompany) return;
    setPublishStatus('loading');
    
    try {
      // 1. Sjekk om bedriften allerede finnes i Supabase
      const { data: existing, error: fetchError } = await supabase
        .from('companies')
        .select('id')
        .eq('id', currentCompany.id)
        .single();
        
      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      const companyPayload = {
        id: currentCompany.id,
        name: formData.name,
        org_number: formData.org_number,
        email: formData.email,
        contact_person: formData.contact_person,
        employees: formData.employees,
        active: true,
        raw_data: {
          address: formData.address,
          industry: formData.industry,
          ...currentCompany
        }
      };

      let error;
      if (existing) {
        const { error: updateError } = await supabase
          .from('companies')
          .update(companyPayload)
          .eq('id', currentCompany.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('companies')
          .insert([companyPayload]);
        error = insertError;
      }

      if (error) throw error;
      
      setPublishStatus('success');
      setTimeout(() => setPublishStatus(''), 3000);
    } catch (err) {
      console.error('Feil ved publisering til sky:', err);
      setPublishStatus('error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500">Laster profil...</p>
      </div>
    );
  }

  if (!currentCompany && !isLoading) {
    return (
      <div className="p-8 bg-red-50 border border-red-200 rounded-xl text-center">
        <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-red-500 text-3xl mx-auto mb-2" />
        <h3 className="font-bold text-red-900">Fant ikke bedriftsprofil</h3>
        <p className="text-red-700 text-sm mb-4">Vi fant ingen bedrift knyttet til din bruker ({user?.email}).</p>
        <button onClick={() => window.location.reload()} className="bg-white border border-red-300 text-red-700 px-4 py-2 rounded-lg text-sm font-bold">
          Last siden på nytt
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bedriftsprofil</h1>
          <p className="text-sm text-gray-500">Administrer nøkkelinformasjon om bedriften.</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handlePublishToCloud}
            disabled={publishStatus === 'loading'}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg shadow-sm text-white transition-colors ${
              publishStatus === 'success' ? 'bg-green-600' : 
              publishStatus === 'error' ? 'bg-red-600' : 
              'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            <SafeIcon icon={publishStatus === 'success' ? FiCheckCircle : FiCloud} className="mr-2" />
            {publishStatus === 'loading' ? 'Lagrer...' : 
             publishStatus === 'success' ? 'Lagret i skyen!' : 
             publishStatus === 'error' ? 'Feil' : 'Publiser endringer'}
          </button>
          <button 
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <SafeIcon icon={isEditing ? FiX : FiEdit2} className="mr-2" />
            {isEditing ? 'Avbryt' : 'Rediger'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isEditing ? (
          <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Bedriftsnavn</label>
                <div className="relative">
                  <SafeIcon icon={FiBuilding} className="absolute left-3 top-3 text-gray-400" />
                  <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Organisasjonsnummer</label>
                <div className="relative">
                  <SafeIcon icon={FiTag} className="absolute left-3 top-3 text-gray-400" />
                  <input type="text" name="org_number" value={formData.org_number} onChange={handleChange} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Kontakt E-post</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-3 text-gray-400" />
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Kontaktperson</label>
                <div className="relative">
                  <SafeIcon icon={FiUsers} className="absolute left-3 top-3 text-gray-400" />
                  <input type="text" name="contact_person" value={formData.contact_person} onChange={handleChange} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">Adresse</label>
                <div className="relative">
                  <SafeIcon icon={FiMapPin} className="absolute left-3 top-3 text-gray-400" />
                  <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Antall Ansatte</label>
                <input type="number" name="employees" value={formData.employees} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Bransje</label>
                <input type="text" name="industry" value={formData.industry} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-sm">
                Lagre Endringer
              </button>
            </div>
          </form>
        ) : (
          <div className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Bedriftsnavn</h4>
                <p className="text-lg font-bold text-gray-900">{formData.name}</p>
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Organisasjonsnummer</h4>
                <p className="text-lg font-mono text-gray-700">{formData.org_number}</p>
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Kontakt E-post</h4>
                <p className="text-gray-700 flex items-center gap-2"><SafeIcon icon={FiMail} className="text-gray-400"/> {formData.email}</p>
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Kontaktperson</h4>
                <p className="text-gray-700">{formData.contact_person}</p>
              </div>
              <div className="md:col-span-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Adresse</h4>
                <p className="text-gray-700 flex items-center gap-2"><SafeIcon icon={FiMapPin} className="text-gray-400"/> {formData.address || 'Ingen adresse registrert'}</p>
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Ansatte</h4>
                <p className="text-gray-700">{formData.employees}</p>
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Bransje</h4>
                <p className="text-gray-700">{formData.industry || '-'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyProfile;